import {ChangeDetectionStrategy, Component, DestroyRef, effect, inject, signal} from '@angular/core';
import {InputOtp} from "primeng/inputotp";
import {MatFormField, MatInput, MatLabel, MatSuffix} from "@angular/material/input";
import {MatIconButton} from "@angular/material/button";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {ErrorInterface} from "src/app/shared/interfaces/global";
import {HttpClient} from "@angular/common/http";
import {AuthService} from "src/app/shared/services/auth.service";
import {SignalStore} from "src/store/app.store";
import {MatSnackBar} from "@angular/material/snack-bar";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {CallPassword} from "src/app/shared/interfaces/auth";
import {NewtelComponent} from "src/app/pages/auth/newtel/newtel.component";

type AuthDataStep = 'authData' | 'confirm' | 'newTel';

@Component({
  selector: 'app-auth-data',
  imports: [
    InputOtp,
    MatFormField,
    MatIconButton,
    MatInput,
    MatLabel,
    MatSuffix,
    ReactiveFormsModule,
    TranslateModule,
    FormsModule,
    NewtelComponent
  ],
  templateUrl: './auth-data.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './auth-data.component.scss'
})

export class AuthDataComponent {
  readonly store = inject(SignalStore);
  user = this.store.getUser();
  private $http = inject(HttpClient);
  private $auth = inject(AuthService);
  private $toast = inject(MatSnackBar);
  private $t = inject(TranslateService);
  private $destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      if (this.user.id()) {
        this.authData.controls.email.setValue(this.user.email());
        this.authData.controls.phone.setValue(
          // @ts-ignore
          this.user.phone() === '' ? '+' : this.user.phone(),
        );
      }
    });
    effect(() => {
      if (this.step() === 'confirm') {
        this.startResendTimer();
      } else {
        this.stopResendTimer();
      }
    });
    this.authData.controls.phone.valueChanges
      .pipe(takeUntilDestroyed(this.$destroyRef))
      .subscribe((res) => {
        if (!res?.startsWith('+'))
          this.authData.controls.phone.setValue(
            '+' + this.authData.controls.phone.value,
            {emitEvent: false},
          );
      });
    this.$destroyRef.onDestroy(() => this.stopResendTimer());
  }

  authData = new FormGroup({
    email: new FormControl('', [Validators.email]),
    phone: new FormControl('', [
      Validators.maxLength(14),
      Validators.minLength(11),
    ]),
    password: new FormControl(''),
    password_confirm: new FormControl(''),
  });

  step = signal<AuthDataStep>('authData');
  loginType = signal<0 | 1>(0);
  codeError = signal(false);
  errorMessage = signal<ErrorInterface | null>(null);
  has_attempts = signal(true);
  resend = signal(true);
  timer = signal(300);
  hide = signal(true);
  hide_confirm = signal(true);
  newTelData = signal<CallPassword | null>(null);
  handle: any;

  code = '';

  private startResendTimer() {
    this.stopResendTimer();
    this.resend.set(true);
    this.timer.set(300);
    this.handle = setInterval(() => {
      this.timer.update(prev => prev - 1);
      if (this.timer() === 0) {
        this.stopResendTimer();
        this.resend.set(false);
        this.timer.set(300);
      }
    }, 1000);
  }

  private stopResendTimer() {
    if (this.handle) {
      clearInterval(this.handle);
      this.handle = undefined;
    }
  }

  setCode() {
    if (
      (this.loginType() && this.code.length === 5) ||
      (!this.loginType() && this.code.length === 4)
    ) {
      this.sendCode();
    }
  }

  sendCode() {
    this.$http
      .post(
        `auth/${this.loginType() ? 'phone' : 'email'}/change/verify`,
        {verify_code: this.code},
        {
          params: {isSSO: true},
          headers: {
            Authorization: `Bearer ${this.$auth.getTempToken()}`,
          },
        },
      )
      .subscribe({
        next: () => {
          this.store.getUser();
          // this.$store.dispatch(GetUserData());
          this.step.set('authData');
          this.code = '';
          this.$toast.open(this.$t.instant('global.updated'), undefined, {
            duration: 1500,
            panelClass: ['success'],
          });
        },
        error: (res: { error: ErrorInterface }) => {
          this.codeError.set(true);
          this.errorMessage.set(res.error);
          this.has_attempts.set(res.error.status_code !== 429);
        },
      });
  }

  resendCode() {
    this.errorMessage.set(null);
    this.loginType() ? this.changePhone() : this.changeEmail();
  }

  changeEmail() {
    this.$http
      .put<{
        temporary_token: string;
      }>(
        'auth/email/change',
        {email: this.authData.controls.email.value},
        {params: {isSSO: true}},
      )
      .subscribe({
        next: (res) => {
          this.loginType.set(0);
          this.$auth.setTempToken(res.temporary_token);
          this.step.set('confirm');
        },
        error: (res: { error: ErrorInterface }) => {
          this.$toast.open(this.$t.instant(res.error.message), undefined, {
            duration: 1500,
            panelClass: ['error'],
          });
          this.authData.controls.email.setValue(this.user.email());
        },
      });
  }

  changePhone() {
    this.$http
      .put<{
        temporary_token: string;
      }>(
        'auth/phone/change',
        {phone: this.authData.controls.phone.value},
        {params: {isSSO: true}},
      )
      .subscribe({
        next: (res) => {
          this.loginType.set(1);
          this.$auth.setTempToken(res.temporary_token);
          this.step.set('confirm');
        },
        error: (res: { error: ErrorInterface }) => {
          this.$toast.open(this.$t.instant(res.error.message), undefined, {
            duration: 1500,
            panelClass: ['error'],
          });
          this.authData.controls.phone.setValue(this.user.phone());
        },
      });
  }

  deletePhone() {
    this.$http
      .delete('auth/phone', {
        params: {
          isSSO: true,
        },
      })
      .subscribe({
        next: () => {
          this.authData.controls.phone.setValue('');
          this.store.loadUser()
        },
        error: (res: { error: ErrorInterface }) => {
          this.$toast.open(res.error.message, undefined, {
            duration: 5000,
            panelClass: ['error'],
          });
        },
      });
  }

  deleteEmail() {
    this.$http
      .delete('auth/email', {
        params: {
          isSSO: true,
        },
      })
      .subscribe({
        next: () => {
          this.authData.controls.email.setValue(null);
          this.store.loadUser();
        },
        error: (res: { error: ErrorInterface }) => {
          this.$toast.open(res.error.message, undefined, {
            duration: 5000,
            panelClass: ['error'],
          });
        },
      });
  }

  clickEventPassword(event: MouseEvent) {
    this.hide.set(!this.hide());
    event.stopPropagation();
  }

  clickEventPassword_confirm(event: MouseEvent) {
    this.hide_confirm.set(!this.hide_confirm());
    event.stopPropagation();
  }

  setPassword() {
    this.$http
      .put(
        'auth/password/change',
        {
          password: this.authData.controls.password.value,
          password_confirm: this.authData.controls.password_confirm.value,
        },
        {
          params: {
            isSSO: true,
          },
        },
      )
      .subscribe({
        next: () => {
          this.$toast.open(this.$t.instant('global.updated'), undefined, {
            duration: 1500,
            panelClass: ['success'],
          });
          this.authData.controls.password.reset();
          this.authData.controls.password_confirm.reset();
        },
        error: (res: { error: ErrorInterface }) =>
          this.$toast.open(this.$t.instant(res.error.message), undefined, {
            duration: 1500,
            panelClass: ['error'],
          }),
      });
  }

  showNewTel() {
    const body = {
      phone: this.authData.controls.phone.value,
    }

    this.$http.put<CallPassword>('auth/phone/change/call-password-id', body, {
      params: {
        isSSO: true,
      }
    })
      .subscribe({
        next: (res) => {
          this.$auth.setTempToken(res.temporary_token);
          this.newTelData.set(res);
          this.step.set('newTel')
        }
      })
  }
}
